/* eslint-disable @next/next/no-img-element */
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled, { useTheme } from 'styled-components'
import { match } from 'ts-pattern'
import { ImageAsset } from '@/components/common'
import { Typo, TypoVariant } from '@/components/common/typography'
import { LayoutHeader } from '@/components/domains/layouts'
import SeoHeader from '@/components/domains/layouts/header/SeoHeader'
import { ENV_CONFIG } from '@/constants/env/config'
import { LAYOUT_UNITS } from '@/constants/legacy/constLayout'
import ROUTES from '@/constants/legacy/constRoutes'
import { QueryStringKeyEnum } from '@/constants/query-string-key.enum'
import { ToastConst } from '@/constants/toast.const'
import { useTracker } from '@/containers/contexts/EventTrackingProvider'
import { useCustomRouter, useKakao } from '@/containers/hooks'
import { UserContainer } from '@/containers/users/UserContainer'
import { doSetGlobalNoticeModal, doSetGlobalToastContent, doSetHeaderTitle } from '@/stores/reduxUI'
import { Bridge } from '@/utils/bridge/bridge'
import { FitpetMallEventEnum } from '@/utils/event-tracker/events'
import appBridgeProvider, { appBridgeLogin, isInAppFlag, isShareAvailable } from '@/utils/utilBridge'
import { textEllipsis } from '@/utils/utilCSS'
import { copy2clipboard } from '@/utils/utilCommon'
import { isDateAfter } from '@/utils/utilDate'
import { numberWithCommas } from '@/utils/utilNumber'

const inviteImagesObject20250602 = {
  topWithActive: 'top_with_active_20250602.png',
  topWithInActive: 'top_with_active_20250602.png',
  notice: 'notice_20250602.png',
  noticeWithInActive: 'notice_20250602.png',
  benefit: 'benefit_20250602.png',
  kakaoShare: 'kakao_share_image_20250602.png',
} as const

const inviteImagesObject20251201 = {
  topWithActive: 'top_with_active_20250602.png',
  topWithInActive: 'top_with_active_20250602.png',
  notice: 'notice_20251201.png',
  noticeWithInActive: 'notice_20251201.png',
  benefit: 'benefit_20251201.png',
  kakaoShare: 'kakao_share_image_20250602.png',
} as const

const inviteImagesObject = {
  topWithActive: 'top_with_active.jpg',
  topWithInActive: 'top_with_inactive_v2.jpg',
  notice: 'notice.jpg',
  noticeWithInActive: 'notice_in_active.jpg',
  benefit: 'benefit_v2.jpg',
  kakaoShare: 'kakao_share_image_v2.jpg',
} as const

type InviteImagesObject = keyof typeof inviteImagesObject

const getFriendImagePath = (key: InviteImagesObject) => {
  const now = new Date().toISOString()

  return match(now)
    .when(
      (date) => isDateAfter(date, new Date('2025-12-01').toISOString()),
      () => `${ENV_CONFIG.s3HostUrl}/${ENV_CONFIG.s3Folder}/friends/friend_invite_${inviteImagesObject20251201[key]}`
    )
    .when(
      (date) => isDateAfter(date, new Date('2025-07-01').toISOString()),
      () => `${ENV_CONFIG.s3HostUrl}/${ENV_CONFIG.s3Folder}/friends/friend_invite_${inviteImagesObject20250602[key]}`
    )
    .otherwise(() => `${ENV_CONFIG.s3HostUrl}/${ENV_CONFIG.s3Folder}/friends/friend_invite_${inviteImagesObject[key]}`)
}

const inviteOnelinkList = {
  prod: 'https://www.fitpetmall.com/mall/friends/guest',
  dev: 'https://dev.fitpetmall.com/mall/friends/guest',
}

const inviteWebLink =
  process.env.NEXT_PUBLIC_ENVIRONMENT === 'production' ? inviteOnelinkList.prod : inviteOnelinkList.dev

const PageFriendsInvite = () => {
  const { push, asPath, query, isReady } = useCustomRouter()
  const dispatch = useDispatch()
  const tracker = useTracker()
  const bridgeLogin = appBridgeLogin()
  const { color } = useTheme()
  const { me, meIsLoading: isMeLoading } = UserContainer.useContainer()
  const { useKakaoProps } = useKakao()
  const [invitationCode, setInvitationCode] = useState<string>(me?.invitationCode || '')
  const { location = '' } = query
  const isLogin = !!me
  const shareLink = `${inviteWebLink}?invitationCode=${invitationCode}`

  const invitationSituationData = useMemo(
    () => ({
      inviteCount: me?.inviteCount,
      invitedFirstOrderUserCount: me?.invitedFirstOrderUserCount,
      inviteAccumulateCouponAmount: me?.inviteAccumulateCouponAmount,
    }),
    [me]
  )

  const friendEventProperties = useMemo(
    () => ({
      is_login: isLogin,
      signup_friends: invitationSituationData.inviteCount,
      purchase_friends: invitationSituationData.invitedFirstOrderUserCount,
      coupon_amount: invitationSituationData.inviteAccumulateCouponAmount,
    }),
    [isLogin, invitationSituationData]
  )

  // 페이지 타이틀
  useEffect(() => {
    dispatch(doSetHeaderTitle('이벤트 | 친구 초대 - 핏펫몰'))
  }, [dispatch])

  // 초대코드 상태 저장
  useEffect(() => {
    if (me?.invitationCode) {
      setInvitationCode(me.invitationCode)
    }
  }, [me])

  // 이벤트 트래킹
  useEffect(() => {
    if (!isReady || isMeLoading) return
    tracker.triggerCustomEvent(FitpetMallEventEnum.ViewFriendevent, {
      location,
      ...friendEventProperties,
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, location, tracker, isMeLoading])

  // 코드 복사하기(초대코드 복사) - 상단
  const handleCopyInvitationCode = (invitationCode: string) => {
    copy2clipboard(invitationCode)
    tracker.triggerCustomEvent(FitpetMallEventEnum.ClickFriendeventCodecopy, {
      ...friendEventProperties,
    })
    dispatch(
      doSetGlobalToastContent({
        content: '코드가 복사되었어요',
        variant: ToastConst.ToastVariant.withCTA,
      })
    )
  }

  // 앱 공유하기
  const shareInApp = () => {
    appBridgeProvider((bridge: Bridge) =>
      bridge.linkShare({
        title: '이벤트 | 친구 초대 - 핏펫몰',
        link: shareLink,
        event: {
          eventName: FitpetMallEventEnum.ClickFriendeventCodecopy,
          eventParam: { buttonText: '카카오톡으로 친구 초대하기' },
        },
      })
    )
  }

  // 공유 URL 복사
  const handleCopyShareUrl = (shareUrl: string) => {
    copy2clipboard(shareUrl)
    tracker.triggerCustomEvent(FitpetMallEventEnum.ClickFriendeventLinkshare, {
      ...friendEventProperties,
    })
    dispatch(
      doSetGlobalToastContent({
        content: '링크가 복사되었어요',
        variant: ToastConst.ToastVariant.withCTA,
      })
    )
  }

  // 링크 공유하기 - 상단(Web일땐 링크 복사, App일땐 앱 공유하기)
  const handleShareUrlLink = () => {
    return isShareAvailable ? shareInApp() : handleCopyShareUrl(shareLink)
  }

  // 카톡으로 친구 초대하기 - 하단
  const handleClickInviteFriendsWithKakao = () => {
    tracker.triggerCustomEvent(FitpetMallEventEnum.ClickFriendeventKakaoinvite, {
      ...friendEventProperties,
    })
    if (!isLogin) {
      dispatch(
        doSetGlobalNoticeModal({
          buttonType: 'ACTION_CANCEL',
          text: '로그인 후 이용하실 수 있습니다.\n로그인 하시겠습니까?',
          okText: '로그인하기',
          cancelText: '돌아가기',
          onOk: () => {
            if (isInAppFlag) {
              appBridgeProvider((bridge: Bridge) =>
                bridge.login().then(() => {
                  bridgeLogin(window.getToken())
                })
              )
            } else {
              push({
                pathname: ROUTES.ACCOUNTS.LOGIN,
                query: {
                  [QueryStringKeyEnum.ReturnUrl]: asPath,
                },
              })
            }
          },
          visible: true,
        })
      )
    } else {
      // 카톡 공유
      const shareKakao = () => {
        const kakaoShareImageSizes = {
          width: 600,
          height: 800,
        }
        useKakaoProps.share({
          title: `[초대코드:${invitationCode}]`,
          description: '친구가 보낸 초대장으로 지금 가입하면 5,000원을 추가로 드려요!',
          url: shareLink,
          imageUrl: getFriendImagePath('kakaoShare'),
          sizes: {
            width: kakaoShareImageSizes.width,
            height: kakaoShareImageSizes.height,
          },
          buttonTitle: '지금 가입하고 쿠폰 받기',
        })
      }
      shareKakao()
    }
  }

  // 비로그인 상태 보였다가 로그인 상태로 바뀌는 경우가 있어서 로딩시에는 빈화면으로 보여줌
  if (isMeLoading) {
    return null
  }

  return (
    <StyledSection className="friends-invite">
      <SeoHeader
        title="이벤트 | 친구 초대 - 핏펫몰"
        metaDescription="오직 핏펫의 고객님만을 위한 특별한 혜택들을 만나보세요."
        pathname={ROUTES.FRIENDS_INVITE}
      />
      <LayoutHeader />

      {isLogin ? (
        <>
          <img
            className="friends-invite__image"
            src={getFriendImagePath('topWithActive')}
            alt="친구추천 서비스 이벤트(회원)"
          />
          <div className="friends-invite__code">
            <div className="friends-invite__code-text">
              <Typo as="h2" variant={TypoVariant.Heading6Bold} color={color.grayWhite}>
                나의 초대코드
              </Typo>
              <Typo variant={TypoVariant.Heading1} color={color.grayWhite}>
                {invitationCode}
              </Typo>
            </div>
            <div className="friends-invite__code-buttons">
              <button
                type="button"
                className="friends-invite__copy-code friends-invite__code-button"
                onClick={() => handleCopyInvitationCode(invitationCode)}
              >
                <Typo as="span" variant={TypoVariant.Body1Bold} color={color.grayBlack}>
                  코드 복사하기
                </Typo>
                <ImageAsset
                  name="ic_invite_copy"
                  style={{
                    width: '1.4rem',
                    height: '1.6rem',
                    marginLeft: '0.8rem',
                  }}
                  svg
                />
              </button>
              <button
                type="button"
                className="friends-invite__share-link friends-invite__code-button"
                onClick={handleShareUrlLink}
              >
                <Typo as="span" variant={TypoVariant.Body1Bold} color={color.grayWhite}>
                  링크 공유하기
                </Typo>
                <ImageAsset
                  name="ic_invite_share"
                  style={{
                    width: '1.4rem',
                    height: '1.6rem',
                    marginLeft: '0.8rem',
                  }}
                  svg
                />
              </button>
            </div>
          </div>
          <div className="friends-invite__situation">
            <Typo as="h2" variant={TypoVariant.Heading6Bold} color={color.gray900}>
              나의 친구 초대 현황
            </Typo>
            <div className="friends-invite__situation-card friends-invite__situation-card--top">
              <div className="friends-invite__situation-invite-count">
                <Typo variant={TypoVariant.Heading2} color={color.gray900}>
                  {invitationSituationData.inviteCount}
                </Typo>
                <Typo as="h3" variant={TypoVariant.Body3SemiBold} color={color.gray900}>
                  가입한 친구
                </Typo>
              </div>
              <div className="friends-invite__situation-order-count">
                <Typo variant={TypoVariant.Heading2} color={color.gray900}>
                  {invitationSituationData.invitedFirstOrderUserCount}
                </Typo>
                <Typo as="h3" variant={TypoVariant.Body3SemiBold} color={color.gray900}>
                  첫 구매한 친구
                </Typo>
              </div>
            </div>
            <div className="friends-invite__situation-card friends-invite__situation-card--bottom">
              <Typo as="h3" variant={TypoVariant.Body3SemiBold} color={color.gray900}>
                총 적립 쿠폰 금액
              </Typo>
              <div className="friends-invite__situation-coupon-amount">
                <Typo variant={TypoVariant.Heading2} color={color.gray900}>
                  {numberWithCommas(invitationSituationData.inviteAccumulateCouponAmount)}
                </Typo>
                <Typo variant={TypoVariant.Heading6SemiBold} color={color.gray900}>
                  원
                </Typo>
              </div>
            </div>
          </div>
        </>
      ) : (
        <img
          className="friends-invite__image"
          src={getFriendImagePath('topWithInActive')}
          alt="친구추천 서비스 이벤트(비회원)"
        />
      )}

      <img
        className="friends-invite__image"
        src={getFriendImagePath('benefit')}
        alt="친구추천 서비스 이벤트 할인혜택"
      />

      <img
        className="friends-invite__image"
        src={getFriendImagePath(isLogin ? 'notice' : 'noticeWithInActive')}
        alt="친구추천 서비스 이벤트 유의사항"
      />

      <div className="friends-invite__button-wrapper">
        <button type="button" className="friends-invite__button" onClick={handleClickInviteFriendsWithKakao}>
          <Typo as="span" variant={TypoVariant.Body1Bold} color={color.grayBlack}>
            카톡으로 친구 초대하기 →
          </Typo>
        </button>
      </div>
    </StyledSection>
  )
}

const StyledSection = styled.section`
  padding-bottom: calc(env(safe-area-inset-bottom) + 8rem);
  background-color: #d0d0d0;
  .friends-invite {
    &__code {
      padding: 0.8rem 7.8rem 5.6rem;
      background-color: ${({
        theme: {
          color: { blue500 },
        },
      }) => blue500};
      text-align: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
    &__code-text {
      margin-bottom: 2.8rem;
      display: flex;
      flex-direction: column;
    }
    &__code-buttons {
      display: flex;
      flex-direction: column;
      gap: 1.2rem;
    }
    &__code-button {
      cursor: pointer;
      padding: 1.2rem 5.2rem 1.2rem 5.6rem;
      border: none;
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
    }
    &__copy-code {
      background-color: ${({
        theme: {
          color: { grayWhite },
        },
      }) => grayWhite};
    }
    &__share-link {
      background-color: ${({
        theme: {
          color: { grayBlack },
        },
      }) => grayBlack};
    }
    &__situation {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1.2rem;
      padding: 2.8rem 3.6rem 4rem;
      background-color: ${({
        theme: {
          color: { blue50 },
        },
      }) => blue50};
    }
    &__situation-card {
      width: 100%;
      padding: 2.4rem 2rem;
      border-radius: 1.2rem;
      background-color: ${({
        theme: {
          color: { grayWhite },
        },
      }) => grayWhite};
      &--top {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        text-align: center;
      }
      &--bottom {
        display: flex;
        flex-direction: column;
        align-items: center;
      }
    }
    &__situation-invite-count {
      div {
        ${textEllipsis(1)};
      }
      padding-right: 1.6rem;
      width: 50%;
    }
    &__situation-order-count {
      div {
        ${textEllipsis(1)};
      }
      position: relative;
      padding-left: 1.6rem;
      width: 50%;
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        height: 5rem;
        width: 1px;
        background-color: ${({
          theme: {
            color: { gray100 },
          },
        }) => gray100};
      }
    }

    &__situation-coupon-amount {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.2rem;
    }

    &__button-wrapper {
      width: 100%;
      background-color: ${({
        theme: {
          color: { grayWhite },
        },
      }) => grayWhite};
      border-top: 1px solid
        ${({
          theme: {
            color: { gray70 },
          },
        }) => gray70};
      max-width: ${LAYOUT_UNITS.BODY_MAX_WIDTH};
      padding: 1.6rem 1.6rem calc(env(safe-area-inset-bottom) + 1.6rem);
      position: fixed;
      bottom: 0;
    }
    &__button {
      cursor: pointer;
      width: 100%;
      border-radius: 0.8rem;

      background: #fee500;
      border: none;
      padding: 1.3rem 1.6rem;
      text-align: center;
    }
    .truncate {
      ${textEllipsis(1)};
    }
    &__image {
      width: 100%;
      vertical-align: top;
      display: inline-block;
    }
  }
`

export default PageFriendsInvite
